import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Search, TrendingUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

interface Keyword {
  id: string;
  keyword: string;
  search_volume: number;
  difficulty: number;
  current_ranking: number | null;
  target_ranking: number;
  status: string;
  page_url: string;
}

export const KeywordResearch = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [pageUrl, setPageUrl] = useState('');
  const [topic, setTopic] = useState('');
  const [keywords, setKeywords] = useState<Keyword[]>([]);

  useEffect(() => {
    fetchKeywords();
  }, []);

  const fetchKeywords = async () => {
    const { data, error } = await supabase
      .from('seo_keywords')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching keywords:', error);
    } else {
      setKeywords(data || []);
    }
  };

  const handleResearch = async () => {
    if (!pageUrl || !topic) {
      toast({
        title: 'Missing Information',
        description: 'Please provide both page URL and topic',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('seo-automation', {
        body: {
          action: 'keyword_research',
          data: { pageUrl, topic }
        }
      });

      if (error) throw error;

      toast({
        title: 'Research Complete',
        description: `Found ${data.keywords.length} relevant keywords`,
      });

      await fetchKeywords();
      setPageUrl('');
      setTopic('');
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: 'Error',
        description: 'Failed to perform keyword research',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyColor = (difficulty: number) => {
    if (difficulty < 30) return 'bg-green-500';
    if (difficulty < 70) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="w-5 h-5" />
            Keyword Research & Optimization
          </CardTitle>
          <CardDescription>
            Automatically identify and track relevant keywords for your pages
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="pageUrl">Page URL</Label>
              <Input
                id="pageUrl"
                placeholder="https://example.com/page"
                value={pageUrl}
                onChange={(e) => setPageUrl(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="topic">Topic/Content Focus</Label>
              <Input
                id="topic"
                placeholder="e.g., smart toilets, luxury bathrooms"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
              />
            </div>
          </div>
          <Button onClick={handleResearch} disabled={loading} className="w-full">
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Researching Keywords...
              </>
            ) : (
              <>
                <Search className="w-4 h-4 mr-2" />
                Research Keywords
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Tracked Keywords ({keywords.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Keyword</TableHead>
                <TableHead>Page</TableHead>
                <TableHead>Volume</TableHead>
                <TableHead>Difficulty</TableHead>
                <TableHead>Current Rank</TableHead>
                <TableHead>Target Rank</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {keywords.map((kw) => (
                <TableRow key={kw.id}>
                  <TableCell className="font-medium">{kw.keyword}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                    {kw.page_url}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <TrendingUp className="w-4 h-4 text-green-500" />
                      {kw.search_volume}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getDifficultyColor(kw.difficulty)}>
                      {kw.difficulty}/100
                    </Badge>
                  </TableCell>
                  <TableCell>{kw.current_ranking || '-'}</TableCell>
                  <TableCell>{kw.target_ranking}</TableCell>
                  <TableCell>
                    <Badge variant={kw.status === 'active' ? 'default' : 'secondary'}>
                      {kw.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {keywords.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No keywords tracked yet. Start by researching keywords above.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};